import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Home as HomeIcon, Presentation as PresentationIcon, Target, CalendarClock, MessageSquareText,
  Mail, FolderOpen, Sparkles, Users, User as UserIcon, MoreHorizontal, ChevronDown, LogOut, Bell,
  Globe, HelpCircle, Star, PanelLeftClose, PanelLeftOpen, Building2, Clock,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { useMe, useWorkspaceHome, useUpdateMyTimezone } from "../../lib/rehearsalRoomQueries";
import { useFeedbackUnreadCount } from "../../lib/feedbackQueries";
import { useMessageThreads } from "../../lib/messagingQueries";
import { useNotifications, useNotificationUnreadCount, useOpenNotification, useResetOnboarding } from "../../lib/notificationQueries";
import { rrLogout } from "../../lib/rehearsalRoomAuth";
import { toast } from "sonner";

const DESTINATION_ICON = {
  home: HomeIcon, presentation: PresentationIcon, practice: Target, sessions: CalendarClock,
  feedback: MessageSquareText, messages: Mail, files: FolderOpen, presence_lab: Sparkles,
  our_work: Users, my_work: UserIcon,
};

const destinationRoute = (workspaceId, key) => {
  if (key === "home") return `/rehearsal-room/w/${workspaceId}/home`;
  // Master v5 Prompt 21 — canonical route is `/presence` (blueprint),
  // never the generic underscore->hyphen `/presence-lab` — that path
  // still exists only as a redirect alias in RehearsalRoomApp.jsx.
  if (key === "presence_lab") return `/rehearsal-room/w/${workspaceId}/presence`;
  return `/rehearsal-room/w/${workspaceId}/${key.replace(/_/g, "-")}`;
};

const SIDEBAR_COLLAPSE_KEY = "rr_sidebar_collapsed";
const MOBILE_PRIMARY_COUNT = 4;

export default function ClientShell({ children }) {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const { data: me, isError: meError } = useMe();
  const { data: home } = useWorkspaceHome(workspaceId);
  const { data: unread } = useFeedbackUnreadCount(workspaceId);
  const feedbackUnreadCount = unread?.count || 0;
  const { data: threadsData } = useMessageThreads(workspaceId);
  const messagesUnreadCount = (threadsData?.items || []).reduce((sum, t) => sum + (t.unread_count || 0), 0);
  const { data: notifUnread } = useNotificationUnreadCount(workspaceId);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1");
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // A dead/expired/missing token must never hang on an infinite "Loading
  // workspace…" — useMe() 401s immediately (it has no `enabled` gate),
  // so this fires as soon as that resolves, covering every page ClientShell
  // wraps (Home, Practice layer, and every ComingSoon destination).
  if (meError) return <Navigate to="/rehearsal-room/login" replace />;

  const destinations = home?.navigation?.destinations || [];
  const primaryMobile = destinations.slice(0, MOBILE_PRIMARY_COUNT);
  const moreMobile = destinations.slice(MOBILE_PRIMARY_COUNT);

  async function handleLogout() {
    await rrLogout();
    navigate("/rehearsal-room/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-paper flex" data-testid="client-shell">
      <aside
        className={`hidden md:flex flex-col bg-ink text-paper shrink-0 transition-[width] duration-200 ${collapsed ? "w-[76px]" : "w-64"}`}
        data-testid="client-shell-sidebar"
      >
        <div className="flex items-center justify-between px-4 py-5">
          {!collapsed && <span className="font-serif text-lg tracking-wide">InterAct</span>}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="text-paper/60 hover:text-paper transition-colors"
            data-testid="client-shell-sidebar-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-1 mt-2" data-testid="client-shell-sidebar-nav">
          {destinations.map((d) => {
            const Icon = DESTINATION_ICON[d.key] || HomeIcon;
            const route = destinationRoute(workspaceId, d.key);
            const active = currentPath === route || (d.key === "presentation" && currentPath.startsWith(`${route}/`));
            return (
              <button
                key={d.key}
                onClick={() => navigate(destinationRoute(workspaceId, d.key))}
                data-testid={`client-shell-nav-${d.key}`}
                title={d.label}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors focus-visible:outline-2 focus-visible:outline-mustard ${
                  active ? "bg-paper/15 text-paper" : "text-paper/70 hover:bg-paper/10 hover:text-paper"
                }`}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span className="truncate flex-1 text-left">{d.label}</span>}
                {d.key === "feedback" && feedbackUnreadCount > 0 && (
                  <span className="bg-terracotta text-paper text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="client-shell-feedback-unread-badge">
                    {feedbackUnreadCount}
                  </span>
                )}
                {d.key === "messages" && messagesUnreadCount > 0 && (
                  <span className="bg-terracotta text-paper text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="client-shell-messages-unread-badge">
                    {messagesUnreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="px-2 pb-5 pt-3 border-t border-paper/10">
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/review`)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-paper/75 hover:text-mustard hover:bg-paper/5 transition-colors"
            data-testid="client-shell-review-action"
          >
            <Star size={15} className="shrink-0" />
            {!collapsed && <span className="font-hand text-base leading-none">Review programme &amp; coach</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-ink/10 bg-paper/90 backdrop-blur-sm sticky top-0 z-30"
          data-testid="client-shell-topbar"
        >
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta">InterAct Globally</p>
            <h1 className="font-serif text-lg text-ink truncate" data-testid="client-shell-workspace-title">
              {home?.title || "Rehearsal Room"}
            </h1>
          </div>
          <ProfileMenu me={me} home={home} onLogout={handleLogout} navigate={navigate} workspaceId={workspaceId} notifUnreadCount={notifUnread?.count || 0} />
        </header>

        <main className="flex-1 px-4 sm:px-6 py-6 pb-24 md:pb-8">{children}</main>
      </div>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 bg-ink border-t border-paper/10 flex items-stretch z-40"
        data-testid="client-shell-mobile-nav"
      >
        {primaryMobile.map((d) => {
          const Icon = DESTINATION_ICON[d.key] || HomeIcon;
          return (
            <button
              key={d.key}
              onClick={() => navigate(destinationRoute(workspaceId, d.key))}
              className="flex-1 flex flex-col items-center gap-1 py-2.5 text-paper/70 hover:text-paper text-[11px] relative"
              data-testid={`client-shell-mobile-nav-${d.key}`}
            >
              <span className="relative">
                <Icon size={18} />
                {d.key === "feedback" && feedbackUnreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 bg-terracotta text-paper text-[9px] font-semibold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="client-shell-mobile-feedback-unread-badge">
                    {feedbackUnreadCount}
                  </span>
                )}
                {d.key === "messages" && messagesUnreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 bg-terracotta text-paper text-[9px] font-semibold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="client-shell-mobile-messages-unread-badge">
                    {messagesUnreadCount}
                  </span>
                )}
              </span>
              {d.label}
            </button>
          );
        })}
        {moreMobile.length > 0 && (
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className="flex-1 flex flex-col items-center gap-1 py-2.5 text-paper/70 hover:text-paper text-[11px]"
                data-testid="client-shell-mobile-nav-more"
              >
                <MoreHorizontal size={18} />
                More
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-paper" data-testid="client-shell-more-sheet">
              <SheetHeader><SheetTitle className="font-serif text-ink">More</SheetTitle></SheetHeader>
              <div className="grid grid-cols-3 gap-3 py-4">
                {moreMobile.map((d) => {
                  const Icon = DESTINATION_ICON[d.key] || HomeIcon;
                  return (
                    <button
                      key={d.key}
                      onClick={() => { setMoreOpen(false); navigate(destinationRoute(workspaceId, d.key)); }}
                      className="flex flex-col items-center gap-2 py-4 border border-ink/10 rounded-lg text-ink-soft hover:border-terracotta hover:text-terracotta transition-colors"
                      data-testid={`client-shell-more-sheet-${d.key}`}
                    >
                      <Icon size={20} />
                      <span className="text-xs text-center">{d.label}</span>
                    </button>
                  );
                })}
                <button
                  onClick={() => { setMoreOpen(false); navigate(`/rehearsal-room/w/${workspaceId}/review`); }}
                  className="flex flex-col items-center gap-2 py-4 border border-ink/10 rounded-lg text-ink-soft hover:border-mustard hover:text-mustard-dark transition-colors"
                  data-testid="client-shell-more-sheet-review"
                >
                  <Star size={20} />
                  <span className="text-xs text-center font-hand">Review</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </nav>
    </div>
  );
}

// Master v5 Prompt 31.1 — "Client Timezone on File". Common Intl-
// supported zones covering every populated region; falls back to just
// the browser-detected zone if the runtime doesn't support
// Intl.supportedValuesOf (older engines).
const TIMEZONE_OPTIONS = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [Intl.DateTimeFormat().resolvedOptions().timeZone];
  }
})();

function ProfileMenu({ me, home, onLogout, navigate, workspaceId, notifUnreadCount = 0 }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tzOpen, setTzOpen] = useState(false);
  const [tzChoice, setTzChoice] = useState(me?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const updateTimezone = useUpdateMyTimezone();

  // Radix DropdownMenu is modal and locks body pointer-events until it
  // finishes closing; opening a Dialog synchronously from onSelect races
  // that cleanup and leaves the whole page unclickable. Close the menu
  // first and only open the Dialog on the next tick, once Radix has
  // released the pointer-events lock.
  function openAfterMenuCloses(setOpen) {
    setMenuOpen(false);
    setTimeout(() => setOpen(true), 120);
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setNotifOpen(true)}
          className="relative p-2 rounded-full hover:bg-ink/5 transition-colors text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-terracotta"
          aria-label="Notifications"
          data-testid="client-shell-notification-bell"
        >
          <Bell size={18} />
          {notifUnreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-paper text-[9px] font-semibold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="client-shell-notification-unread-badge">
              {notifUnreadCount}
            </span>
          )}
        </button>

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-ink/5 transition-colors focus-visible:outline-2 focus-visible:outline-terracotta"
              data-testid="client-shell-profile-trigger"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-terracotta text-paper text-xs font-medium">
                  {(me?.name || "?").slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <ChevronDown size={14} className="text-ink-soft" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="client-shell-profile-menu">
            <DropdownMenuLabel className="text-ink-soft text-xs font-normal truncate">{me?.name || "Account"}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {home?.has_other_workspaces && (
              <DropdownMenuItem onClick={() => navigate("/rehearsal-room")} data-testid="client-shell-menu-switch-workspace">
                <Building2 size={15} className="mr-2" /> Switch workspace
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => navigate(`/rehearsal-room/w/${home?.wrk_id}/account/commercial`)}
              data-testid="client-shell-menu-commercial-center"
            >
              <Building2 size={15} className="mr-2" /> Commercial Center
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openAfterMenuCloses(setNotifOpen); }} data-testid="client-shell-menu-notifications">
              <Bell size={15} className="mr-2" /> Notifications
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openAfterMenuCloses(setLangOpen); }} data-testid="client-shell-menu-language">
              <Globe size={15} className="mr-2" /> Language &amp; accessibility
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTzChoice(me?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone); openAfterMenuCloses(setTzOpen); }} data-testid="client-shell-menu-timezone">
              <Clock size={15} className="mr-2" /> Timezone
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openAfterMenuCloses(setHelpOpen); }} data-testid="client-shell-menu-help">
              <HelpCircle size={15} className="mr-2" /> Help
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="client-shell-menu-sign-out">
              <LogOut size={15} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
        <DialogContent data-testid="client-shell-notifications-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Notifications</DialogTitle>
            <DialogDescription>Every notification deep-links to exactly where it happened.</DialogDescription>
          </DialogHeader>
          <NotificationsList workspaceId={workspaceId} navigate={navigate} onNavigate={() => setNotifOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={langOpen} onOpenChange={setLangOpen}>
        <DialogContent data-testid="client-shell-language-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Language &amp; accessibility</DialogTitle>
            <DialogDescription>
              English is currently the only available language. More language and accessibility
              options are on the way.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={tzOpen} onOpenChange={setTzOpen}>
        <DialogContent data-testid="client-shell-timezone-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Timezone</DialogTitle>
            <DialogDescription>
              Set your own timezone once — every Session time and confirmation email will show it
              consistently, instead of guessing from your browser each time.
            </DialogDescription>
          </DialogHeader>
          <select
            value={tzChoice}
            onChange={(e) => setTzChoice(e.target.value)}
            className="border border-input rounded-md h-9 px-2 text-sm bg-background w-full"
            data-testid="client-shell-timezone-select"
          >
            {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={async () => {
                try {
                  await updateTimezone.mutateAsync(tzChoice);
                  toast.success("Timezone updated");
                  setTzOpen(false);
                } catch {
                  toast.error("Couldn't update your timezone");
                }
              }}
              disabled={updateTimezone.isPending}
              className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
              data-testid="client-shell-timezone-save-btn"
            >
              {updateTimezone.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent data-testid="client-shell-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-ink">Help</DialogTitle>
            <DialogDescription>
              Questions about your Rehearsal Room? Message your coach directly, or reach the
              InterAct team at support@interactglobally.com.
            </DialogDescription>
          </DialogHeader>
          <ReplayTourSection workspaceId={workspaceId} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function NotificationsList({ workspaceId, navigate, onNavigate }) {
  const { data, isPending } = useNotifications(workspaceId);
  const openNotification = useOpenNotification(workspaceId);
  const items = data?.items || [];

  async function handleOpen(n) {
    try {
      const result = await openNotification.mutateAsync(n.ntf_id);
      onNavigate();
      if (result.route === "messages") {
        navigate(`/rehearsal-room/w/${workspaceId}/messages`);
      } else if (result.route === "room" && result.launch_id) {
        navigate(`/rehearsal-room/w/${workspaceId}/room/${result.launch_id}`);
      } else if (result.route === "sessions") {
        // Master v5 Prompt 32 — "Book Session" / "Start Session Now".
        // "booked" is already SessionsPage's default tab.
        navigate(`/rehearsal-room/w/${workspaceId}/sessions`);
      } else if (result.feedback_id) {
        navigate(`/rehearsal-room/w/${workspaceId}/feedback/${result.feedback_id}`);
      }
    } catch {
      // A revoked/expired target returns a non-enumerating 404 —
      // nothing to navigate to; the notification stays in the list.
    }
  }

  if (isPending) return <p className="text-sm text-ink-soft" data-testid="client-shell-notifications-loading">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-ink-soft" data-testid="client-shell-notifications-empty">You have no new notifications yet.</p>;

  return (
    <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="client-shell-notifications-list">
      {items.map((n) => (
        <button
          key={n.ntf_id}
          onClick={() => handleOpen(n)}
          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${n.read_at ? "text-ink-soft hover:bg-ink/5" : "font-medium text-ink bg-terracotta/5 hover:bg-terracotta/10"}`}
          data-testid={`client-shell-notification-item-${n.ntf_id}`}
        >
          {n.title}
          <span className="block text-[11px] text-ink-soft font-normal mt-0.5">{new Date(n.created_at).toLocaleString()}</span>
        </button>
      ))}
    </div>
  );
}

const REPLAYABLE_FEATURES = [
  { key: "sessions", label: "Sessions" },
  { key: "feedback", label: "Feedback" },
  { key: "files", label: "Files" },
  { key: "messages", label: "Messages" },
];

function ReplayTourSection({ workspaceId }) {
  const resetOnboarding = useResetOnboarding(workspaceId);
  return (
    <div className="pt-2 border-t border-ink/10" data-testid="client-shell-help-replay-tours">
      <p className="text-xs font-medium text-ink-soft mb-2">Replay a tour</p>
      <div className="flex flex-wrap gap-2">
        {REPLAYABLE_FEATURES.map((f) => (
          <button
            key={f.key}
            onClick={() => resetOnboarding.mutate(f.key)}
            className="text-xs px-2.5 py-1 rounded-full border border-ink/15 text-ink-soft hover:border-terracotta hover:text-terracotta transition-colors"
            data-testid={`client-shell-replay-tour-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );
}
